import React, { useEffect } from 'react';
import { Filter } from '@components';
import { Button } from '@primitives';
import styles from './styles.module.scss';
import VerticalSeperator from '../../../../components/VerticalSeperator';
import { DownloadIcon } from '@icons';
import { UploadIcon } from '@icons';
import axiosInstance, { ApiKeys, getApiUrl } from '../../../../utils/ApiHelper';
import { Agency, Downloadtype, State } from '../../types';
import { useSelector } from 'react-redux';
import SingleAutocompleteDropdown from '../../../../components/AutocompleteDropdown/SingleAutocompleteDropdown';
import { useDispatch } from 'react-redux';
import {
  setAgencies,
  setSelectedAgency,
  setSelectedState
} from '../../../../reducers/agencyPincodeMappingSllice';
import { RootState } from '../../../../store';
import { toast } from '@primitives/Toast';
import FilterIcon from '../../../../assets/images/icons/FilterIcon';
import toProperCase from '@cp/src/utils/toProperCase';

type DataTableHeaderProps = {
  openUploadDialog: () => void;
};

export const transformStates = (states: any) => {
  return states.map((state: string) => {
    return {
      label: toProperCase(state?.toLowerCase()),
      value: state
    };
  });
};

export const transformAgencies = (agencies: any) => {
  return agencies.map((agency: Agency) => {
    return {
      label: agency.name,
      value: agency.code
    };
  });
};

function DataTableHeader({ openUploadDialog }: DataTableHeaderProps) {
  const selectedAgency = useSelector(
    (state: RootState) => state.agencyPincodeMapping.selectedAgency
  );
  const selectedState = useSelector((state: RootState) => state.agencyPincodeMapping.selectedState);

  const states = useSelector((state: RootState) => state.agencyPincodeMapping.states);
  const agencies = useSelector((state: RootState) => state.agencyPincodeMapping.agencies);
  const dispatch = useDispatch();

  const onMyServiceabilityDownload = () => {
    if (!selectedAgency) {
      toast('Please select an agency first', { type: 'error' });
      return;
    }
    toast('Downloading file please wait', {
      type: 'info',
      icon: <DownloadIcon color={'var(--blue-base)'} />
    });
    const url = getApiUrl(ApiKeys.AGENCY_SERVICEABILITY_GET_STATIC_URLS);
    axiosInstance
      .get(url, {
        params: { agencyCode: selectedAgency, downloadType: Downloadtype.MY_SERVICEBILITY }
      })
      .then(res => {
        window.open(res?.data?.preSignedGetUrl, '_blank');
      })
      .catch(err => {
        toast('Unable to download file please try again', { type: 'error' });
      });
  };

  const handleOnAgencyChange = (agency: string) => {
    dispatch(setSelectedAgency(agency));
  };

  const handleOnStateChange = (state: string) => {
    dispatch(setSelectedState(state));
  };

  const onAllPinCodesDownload = () => {
    toast('Downloading file please wait', {
      type: 'info',
      icon: <DownloadIcon color={'var(--blue-base)'} />
    });
    const url = getApiUrl(ApiKeys.AGENCY_SERVICEABILITY_GET_STATIC_URLS);
    axiosInstance
      .get(url, { params: { downloadType: Downloadtype.ALL_PINCODES } })
      .then(res => {
        window.open(res?.data?.preSignedGetUrl, '_blank');
      })
      .catch(err => {
        toast('Unable to download file please try again', { type: 'error' });
      });
  };

  return (
    <div className={styles.dataTableHeaderContainer}>
      <div className={styles.leftContainer}>
        <SingleAutocompleteDropdown
          key={'agencies'}
          placeholder={'Select Agency'}
          selectedOption={selectedAgency}
          options={transformAgencies(agencies)}
          onSelectionChange={option => handleOnAgencyChange(option?.value || '')}
          containerClasses={styles.dropdown}
        />
        <SingleAutocompleteDropdown
          title={toProperCase(selectedState?.toLowerCase()) || 'Filter state'}
          onSelectionChange={option => handleOnStateChange(option?.value || '')}
          options={transformStates(states)}
          selectedOption={selectedState || ''}
          containerClasses={styles.dropdown}
          placeholder={toProperCase(selectedState?.toLowerCase()) || 'Filter state'}
        />
      </div>

      <div className={styles.rightContainer}>
        <Button
          onClick={onAllPinCodesDownload}
          variant="text"
          startAdornment={<DownloadIcon color="var(--blue-base)" />}
        >
          All pincodes
        </Button>
        <Button
          onClick={onMyServiceabilityDownload}
          variant="secondary"
          startAdornment={<DownloadIcon color={'var(--grayscale-2)'} />}
        >
          Current agency serviceability
        </Button>
        <Button
          onClick={openUploadDialog}
          variant="primary"
          startAdornment={<UploadIcon color="white" />}
        >
          Update Serviceability
        </Button>
      </div>
    </div>
  );
}

export default DataTableHeader;
